import schema from 'bike-schema'
import { t, getLang } from './i18n'
import { BaseModel, Color, PriceSettings, Country } from 'bike-schema'
import { BaseModelId, Component, Option } from 'shared'

const enabledColors: Color[] = schema.colors.filter((color) => !color.disabled)

export function getLogoPrice(currencyCode: string): number {
  return schema.settings.logo_prices[currencyCode]
}

export function getCustomColorPrice(
  currencyCode: string,
  baseModelId = ''
): number {
  return baseModelId.includes('cargo')
    ? schema.settings.custom_color_prices_cargo[currencyCode]
    : schema.settings.custom_color_prices[currencyCode]
}

export function getElechicLogoPrice(): number {
  return schema.settings.elechic_logo_price
}

export function listBaseModels(): BaseModel[] {
  return schema.base_models
}

export function getBaseModel(id: BaseModelId): BaseModel | undefined {
  return schema.base_models.find((bm) => bm.id === id)
}

export function getBatteryPrices(): PriceSettings {
  return schema.settings.small_battery_prices
}

export function getSaddlePrices(): PriceSettings {
  return schema.settings.saddle_prices
}

export function getMenuGroups(): any[] {
  return schema.menu_groups
}

export function getComponent(
  baseModel: BaseModel | undefined,
  id: string
): Component | undefined {
  return baseModel?.components.find((component) => component.id === id)
}

export function getOption(
  component: Component | undefined,
  id: string
): Option | undefined {
  return component?.options.find((option) => option.id === id)
}

export function getVimeoId(id: string): string | null {
  const component = schema.base_models.find((bm) => bm.id === id)
  if (component?.vimeo_id) {
    return component.vimeo_id
  }
  return null
}

export function listColors(): Color[] {
  return enabledColors
}

export function listUrbanChicGentsColors(): Color[] {
  return [
    {
      ral: 1019,
      hex: '#c3b5a7',
      name: 'Grey beige'
    },
    {
      ral: 6009,
      hex: '#31372B',
      name: 'Fir green'
    },
    {
      ral: 7005,
      hex: '#646B63',
      name: 'Mouse grey'
    },
    {
      ral: 9005,
      hex: '#0A0A0A',
      name: 'Jet black'
    }
  ]
}

export function listUrbanChicLadiesColors(): Color[] {
  return [
    {
      ral: 1019,
      hex: '#c3b5a7',
      name: 'Grey beige'
    },
    {
      ral: 3020,
      hex: '#CC0605',
      name: 'Traffic red'
    },
    {
      ral: 7005,
      hex: '#646B63',
      name: 'Mouse grey'
    },
    {
      ral: 9005,
      hex: '#0A0A0A',
      name: 'Jet black'
    }
  ]
}

export function getColor(ral: number): Color | undefined {
  return enabledColors.find((color) => color.ral === ral)
}

export function listColorCategories(): {
  title: string | number
  name: string
}[] {
  return [
    'L',
    'S',
    'Q',
    1000,
    2000,
    3000,
    4001,
    5000,
    6000,
    7000,
    8000,
    9001
  ].map((title) => ({
    title,
    name: t(`ral_color_category_${title}`)
  }))
}

export function listCountries(): Country[] {
  return schema.countries
}

export function getCountry(code: string): Country | undefined {
  return schema.countries.find((country) => code === country.code)
}

export function listCurrencies(): any[] {
  return schema.currencies
}

export function getTranslation(translations: {
  [key: string]: string
}): string {
  return translations[getLang()] || translations.en
}

export function getSpecification(
  baseModelId: string,
  model: string,
  option: string,
  lang: string
): [string, string] {
  const components = schema.base_models.find(
    (model) => baseModelId === model.id
  )?.components
  const choice = components?.find((choices) => model === choices.id)
  const name = choice?.name[lang]
  const currOpt = choice?.options.find((opt) => option === opt.id)
  if (!currOpt?.specification) return ['No data', name || '']
  return [currOpt.specification[lang], name || '']
}

const listOfColorableOptions = [
  'custom',
  'basket',
  'large',
  'cage',
  'high',
  'low'
]

export function getSpecifications(bikeConfig: any, lang: string): any[] {
  const baseModelId = bikeConfig.baseModelId
  const choices = bikeConfig.choices
  const components = schema.base_models.find(
    (model) => baseModelId === model.id
  )?.components
  let choiceArr: any[] = []
  choices.forEach((choice: any) => {
    const currentRal = choice.ral
    let currentColor: string | null = null
    const component = components?.find(
      (element) => element.id === choice.componentId
    )
    const currSpec = component?.options.find(
      (element) => element.id === choice.optionId
    )

    const isOneOf = listOfColorableOptions.find(
      (element) => element === choice.optionId
    )

    if (isOneOf && currentRal !== undefined) {
      const color = getColor(currentRal)
      currentColor = `${color?.name} RAL:${color?.ral}`
    }

    if (currSpec?.id.includes('custom') && currentRal !== undefined) {
      const color = getColor(currentRal)
      currentColor = `${color?.name} RAL:${color?.ral}`
    }

    if (
      (component?.id !== 'kickstand' && currSpec?.id === 'single') ||
      currSpec?.id === 'double'
    ) {
      const color = getColor(currentRal)
      currentColor = `${color?.name} RAL:${color?.ral}`
    }

    if (currSpec?.specification) {
      choiceArr = [
        ...choiceArr,
        [
          component?.name.en,
          currentColor ? currentColor : currSpec.name.en,
          currSpec.specification.en
        ]
      ]
    }
  })

  const additionalInfo = getAdditionalInfo(baseModelId, lang)

  additionalInfo.forEach((info: string) => {
    let arr = info.split(':')
    choiceArr = [
      ...choiceArr,
      [
        arr[0],
        baseModelId.startsWith('urban')
          ? getUrbanColor(choices[0].ral, arr[0])
          : '',
        arr[1]
      ]
    ]
  })
  let updatedArr = [...choiceArr]
  if (bikeConfig.initials)
    updatedArr.push(['Name plate initials', `${bikeConfig.initials}`])
  return updatedArr
}

function getUrbanColor(currentRal: number, choiceName: string): string {
  const colourableSelection = ['Frame', 'Fork', 'Mudguards', 'Chain guard']
  const color = getColor(currentRal)
  if (colourableSelection.includes(choiceName))
    return `RAL: ${color?.ral} ${color?.name}`
  return ''
}

export function getAdditionalInfo(baseModelId: string, lang: string): string[] {
  const component = schema.base_models.find((model) => baseModelId === model.id)
  if (component?.additional_info) {
    return component.additional_info[lang]
  }
  return ['No data']
}

export const getDefaultPopover = (baseModelId: string): string | undefined => {
  if (getVimeoId(baseModelId)) {
    return 'video'
  }
  if (baseModelId.startsWith('cargo')) {
    return undefined
  }
  return 'presets'
}
